// Small Climb-branded (dark) UI kit shared across screens.

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import type { Risk } from '@dwa/shared';

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' }) {
  const base =
    'inline-flex items-center justify-center rounded-lg px-5 py-2.5 font-medium transition disabled:opacity-40 disabled:cursor-not-allowed';
  const variants = {
    primary: 'bg-climb-coral text-white hover:bg-climb-coral-mid',
    secondary: 'border border-climb-teal-soft/50 text-climb-teal-soft hover:bg-white/5',
    ghost: 'text-climb-teal-soft hover:bg-white/5',
  };
  return <button className={`${base} ${variants[variant]} ${className}`} {...props} />;
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-climb-line-dark bg-climb-surface p-6 ${className}`}>{children}</div>
  );
}

export function TextField({
  label,
  error,
  id,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string }) {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1 block text-sm font-medium text-climb-paper">{label}</span>
      <input
        id={id}
        className="w-full rounded-lg border border-climb-line-dark bg-climb-surface-2 px-3 py-2 text-climb-paper placeholder:text-climb-teal-soft/50 outline-none focus:border-climb-cyan focus:ring-1 focus:ring-climb-cyan"
        aria-invalid={error ? true : undefined}
        {...props}
      />
      {error ? <span className="mt-1 block text-sm text-climb-coral">{error}</span> : null}
    </label>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-climb-teal-soft" role="status">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-climb-cyan border-t-transparent" />
      <span>{label}</span>
    </div>
  );
}

const RISK_STYLES: Record<Risk, string> = {
  High: 'bg-climb-coral/20 text-climb-coral',
  Medium: 'bg-amber-400/15 text-amber-300',
  Low: 'bg-climb-sage/20 text-climb-sage',
};

export function RiskBadge({ risk }: { risk: Risk }) {
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${RISK_STYLES[risk]}`}>{risk} risk</span>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div role="alert" className="rounded-lg bg-climb-coral/20 px-4 py-3 text-sm text-climb-coral">
      {message}
    </div>
  );
}
